import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  MessageCircle, Plus, Send, Check, Reply, Calendar, Clock, AlertCircle,
  Trash2, Edit, ChevronRight, FileText, Filter,
} from "lucide-react";
import PageHeader from "../../components/PageHeader";

const tiposRec = [
  "anticipo", "liquidacion", "contrato", "firma_pendiente", "evento_proximo",
  "seguimiento_post", "resena", "proveedor", "personal", "compra", "general",
];
const prioridades = ["baja", "media", "alta", "urgente"];

const prioColor = {
  baja: "bg-stone-100 text-stone-700",
  media: "bg-blue-100 text-blue-800",
  alta: "bg-amber-100 text-amber-800",
  urgente: "bg-red-100 text-red-800",
};

const today = () => new Date().toISOString().slice(0, 10);

const emptyForm = {
  tipo: "general", titulo: "", descripcion: "", fecha: today(), hora: "",
  whatsapp_telefono: "", whatsapp_mensaje: "", whatsapp_plantilla_id: "",
  prioridad: "media", responsable: "", notas: "",
  cliente_id: "", evento_id: "", cotizacion_id: "", contrato_id: "", pago_id: "",
};

const RecordatoriosWhatsApp = () => {
  const [recs, setRecs] = useState([]);
  const [plantillas, setPlantillas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroTipo, setFiltroTipo] = useState("todos");
  const [filtroEstatus, setFiltroEstatus] = useState("activos");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const fetchData = useCallback(async () => {
    try {
      const [r1, r2, r3, r4] = await Promise.all([
        axios.get(`${API}/recordatorios`),
        axios.get(`${API}/whatsapp/plantillas`),
        axios.get(`${API}/clientes`).catch(() => ({ data: [] })),
        axios.get(`${API}/eventos`).catch(() => ({ data: [] })),
      ]);
      setRecs(r1.data || []);
      setPlantillas(r2.data || []);
      setClientes(r3.data || []);
      setEventos(r4.data || []);
    } catch {
      toast.error("Error al cargar");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = recs.filter((r) => {
    if (filtroTipo !== "todos" && r.tipo !== filtroTipo) return false;
    if (filtroEstatus === "activos") {
      if (r.completado || ["enviado", "completado", "cancelado"].includes(r.estatus)) return false;
    } else if (filtroEstatus === "enviados") {
      if (r.estatus !== "enviado") return false;
    } else if (filtroEstatus === "vencidos") {
      if (r.fecha >= today()) return false;
      if (r.completado) return false;
    } else if (filtroEstatus === "hoy") {
      if (r.fecha !== today()) return false;
    } else if (filtroEstatus === "completados") {
      if (!r.completado && r.estatus !== "completado") return false;
    }
    return true;
  });

  // KPIs
  const hoyCount = recs.filter((r) => r.fecha === today() && !r.completado).length;
  const vencidosCount = recs.filter((r) => r.fecha < today() && !r.completado && r.estatus !== "enviado").length;
  const enviadosCount = recs.filter((r) => r.estatus === "enviado").length;
  const proximosCount = recs.filter((r) => r.fecha > today() && !r.completado).length;

  const openNew = (preset = {}) => {
    setEditing(null);
    setForm({ ...emptyForm, ...preset });
    setDialogOpen(true);
  };

  const openEdit = (rec) => {
    setEditing(rec);
    setForm({ ...emptyForm, ...rec, hora: rec.hora || "" });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.titulo?.trim() || !form.fecha) {
      toast.error("Titulo y fecha son requeridos");
      return;
    }
    try {
      const payload = { ...form };
      // Limpia campos vacios
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      payload.titulo = form.titulo;
      payload.fecha = form.fecha;
      if (editing) {
        await axios.put(`${API}/recordatorios/${editing.recordatorio_id}`, payload);
        toast.success("Recordatorio actualizado");
      } else {
        await axios.post(`${API}/recordatorios`, payload);
        toast.success("Recordatorio creado");
      }
      setDialogOpen(false);
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar recordatorio?")) return;
    try {
      await axios.delete(`${API}/recordatorios/${id}`);
      toast.success("Eliminado");
      fetchData();
    } catch { toast.error("Error"); }
  };

  const handleComplete = async (rec) => {
    try {
      await axios.put(`${API}/recordatorios/${rec.recordatorio_id}`, { completado: true, estatus: "completado" });
      toast.success("Marcado como completado");
      fetchData();
    } catch { toast.error("Error"); }
  };

  const handlePosponer = async (rec) => {
    const dias = parseInt(window.prompt("Posponer cuantos dias?", "1"), 10);
    if (isNaN(dias) || dias <= 0) return;
    const d = new Date(rec.fecha);
    d.setDate(d.getDate() + dias);
    const nuevaFecha = d.toISOString().slice(0, 10);
    try {
      await axios.post(`${API}/recordatorios/${rec.recordatorio_id}/posponer`, { fecha: nuevaFecha });
      toast.success("Pospuesto");
      fetchData();
    } catch { toast.error("Error"); }
  };

  const handleEnviarWhats = async (rec) => {
    // Renderiza el mensaje con datos del cliente/evento
    const cliente = clientes.find((c) => c.cliente_id === rec.cliente_id);
    const evento = eventos.find((e) => e.evento_id === rec.evento_id);
    const variables = {
      nombre_cliente: cliente?.nombre || "",
      fecha_evento: evento?.fecha || rec.fecha,
      hora_evento: evento?.hora_inicio || "",
      tipo_evento: evento?.tipo || "",
      monto: evento?.total ? `$${Number(evento.total).toLocaleString("es-MX")}` : "",
      saldo: evento?.saldo_pendiente ? `$${Number(evento.saldo_pendiente).toLocaleString("es-MX")}` : "",
      anticipo: evento?.anticipo_default ? `$${Number(evento.anticipo_default).toLocaleString("es-MX")}` : "",
      fecha_limite: rec.fecha,
      nombre_evento: evento?.nombre || "",
      responsable: rec.responsable || "",
    };
    try {
      const telefono = rec.whatsapp_telefono || cliente?.whatsapp || cliente?.telefono || "";
      const payload = {
        telefono,
        variables,
        ...(rec.whatsapp_plantilla_id ? { template_id: rec.whatsapp_plantilla_id } : { mensaje: rec.whatsapp_mensaje || rec.titulo }),
      };
      const r = await axios.post(`${API}/whatsapp/preview`, payload);
      if (!r.data.link) {
        toast.error("Telefono no definido para este recordatorio");
        return;
      }
      window.open(r.data.link, "_blank");
      // Auto-marcar como listo_enviar (no marca enviado hasta que confirme)
    } catch {
      toast.error("Error al preparar mensaje");
    }
  };

  const handleMarcarEnviado = async (rec) => {
    try {
      await axios.post(`${API}/recordatorios/${rec.recordatorio_id}/marcar-enviado`);
      toast.success("Marcado como enviado");
      fetchData();
    } catch { toast.error("Error"); }
  };

  const handleRegistrarRespuesta = async (rec) => {
    const respuesta = window.prompt("Respuesta del cliente:", "");
    if (!respuesta) return;
    try {
      await axios.post(`${API}/recordatorios/${rec.recordatorio_id}/registrar-respuesta`, { respuesta });
      toast.success("Respuesta registrada");
      fetchData();
    } catch { toast.error("Error"); }
  };

  // Auto-rellenar mensaje al elegir plantilla
  const onSelectPlantilla = (tplId) => {
    setForm((p) => {
      const tpl = plantillas.find((t) => t.template_id === tplId);
      return { ...p, whatsapp_plantilla_id: tplId, whatsapp_mensaje: tpl?.mensaje || p.whatsapp_mensaje };
    });
  };

  const kpis = [
    { label: "Hoy", value: hoyCount, tone: hoyCount > 0 ? "amber" : "stone", icon: Clock },
    { label: "Vencidos", value: vencidosCount, tone: vencidosCount > 0 ? "red" : "stone", icon: AlertCircle },
    { label: "Proximos", value: proximosCount, tone: "blue", icon: Calendar },
    { label: "Enviados", value: enviadosCount, tone: "emerald", icon: Send },
  ];

  const toneCls = (t) => ({
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    red: "bg-red-50 border-red-200 text-red-800",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    stone: "bg-stone-50 border-stone-200 text-stone-700",
  }[t] || "bg-stone-50 border-stone-200");

  return (
    <div className="space-y-6" data-testid="recordatorios-wa-page">
      <PageHeader
        title="Recordatorios WhatsApp"
        description="A quien escribirle, que mensaje enviar y cuando. Agenda inteligente conectada a clientes, eventos, pagos y contratos. El envio es manual asistido (abre WhatsApp con el mensaje precargado)."
        primaryAction={{
          label: "Nuevo recordatorio",
          icon: Plus,
          testId: "btn-nuevo-rec-wa",
          onClick: () => openNew(),
        }}
        secondaryActions={[
          { label: "Plantillas", icon: FileText, testId: "btn-ir-plantillas", onClick: () => { window.location.href = "/panel/whatsapp-plantillas"; } },
        ]}
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {kpis.map((k) => (
          <Card key={k.label} className={`border ${toneCls(k.tone)}`}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs">{k.label}</p>
                <p className="text-3xl font-display">{k.value}</p>
              </div>
              <k.icon className="w-6 h-6 opacity-60" />
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex gap-2 flex-wrap">
          {["activos", "hoy", "vencidos", "enviados", "completados"].map((f) => (
            <Button key={f} size="sm" variant={filtroEstatus === f ? "default" : "outline"} onClick={() => setFiltroEstatus(f)} className={filtroEstatus === f ? "bg-emerald-900 hover:bg-emerald-800 capitalize" : "capitalize"} data-testid={`filtro-est-${f}`}>
              {f}
            </Button>
          ))}
        </div>
        <Select value={filtroTipo} onValueChange={setFiltroTipo}>
          <SelectTrigger className="w-full sm:w-52 h-9" data-testid="filtro-tipo-wa">
            <Filter className="w-3 h-3 mr-1" /><SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los tipos</SelectItem>
            {tiposRec.map((t) => <SelectItem key={t} value={t} className="capitalize">{t.replace("_", " ")}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Lista */}
      {loading ? (
        <div className="text-center py-8 text-stone-500">Cargando...</div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed border-stone-300">
          <CardContent className="p-8 text-center">
            <MessageCircle className="w-12 h-12 mx-auto text-stone-300 mb-3" />
            <p className="text-stone-600 font-medium">Sin recordatorios en esta vista</p>
            <p className="text-xs text-stone-500 mt-1 mb-3">Crea uno o cambia los filtros.</p>
            <Button onClick={() => openNew()} className="bg-emerald-900 hover:bg-emerald-800"><Plus className="w-4 h-4 mr-1" /> Nuevo recordatorio</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((rec) => {
            const cli = clientes.find((c) => c.cliente_id === rec.cliente_id);
            const ev = eventos.find((e) => e.evento_id === rec.evento_id);
            const tel = rec.whatsapp_telefono || cli?.whatsapp || cli?.telefono;
            const vencido = rec.fecha < today() && !rec.completado && rec.estatus !== "enviado";
            return (
              <Card key={rec.recordatorio_id} className={`border ${vencido ? "border-red-200 bg-red-50" : "border-stone-200"}`} data-testid={`rec-wa-${rec.recordatorio_id}`}>
                <CardContent className="p-3 sm:p-4">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h3 className="font-medium text-stone-900 truncate text-sm sm:text-base">{rec.titulo}</h3>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${prioColor[rec.prioridad] || prioColor.media}`}>{rec.prioridad}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-stone-100 text-stone-600 capitalize">{rec.tipo?.replace("_", " ")}</span>
                        {rec.estatus === "enviado" && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800">Enviado</span>}
                        {rec.completado && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-stone-200 text-stone-600">Completado</span>}
                        {vencido && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-800">Vencido</span>}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-stone-600">
                        <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {rec.fecha} {rec.hora || ""}</span>
                        {cli && <Link to="/panel/clientes" className="text-emerald-800 hover:underline">{cli.nombre}</Link>}
                        {ev && <Link to={`/panel/eventos/${ev.evento_id}`} className="text-emerald-800 hover:underline truncate">Evento: {ev.nombre}</Link>}
                        {tel && <span className="text-stone-500">{tel}</span>}
                      </div>
                      {rec.whatsapp_respuesta && (
                        <div className="mt-2 p-2 bg-stone-50 border border-stone-200 rounded text-xs">
                          <span className="font-semibold text-stone-700">Respuesta:</span> {rec.whatsapp_respuesta}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1 shrink-0">
                      {tel && rec.estatus !== "enviado" && (
                        <Button size="sm" onClick={() => handleEnviarWhats(rec)} className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white text-xs" data-testid={`btn-enviar-wa-${rec.recordatorio_id}`}>
                          <Send className="w-3 h-3 mr-1" /> Enviar WA
                        </Button>
                      )}
                      {rec.estatus !== "enviado" && (
                        <Button size="sm" variant="outline" onClick={() => handleMarcarEnviado(rec)} className="h-9 text-xs" data-testid={`btn-marcar-enviado-${rec.recordatorio_id}`}>
                          <Check className="w-3 h-3 mr-1" /> Marcar enviado
                        </Button>
                      )}
                      {rec.estatus === "enviado" && !rec.whatsapp_respuesta && (
                        <Button size="sm" variant="outline" onClick={() => handleRegistrarRespuesta(rec)} className="h-9 text-xs" data-testid={`btn-respuesta-${rec.recordatorio_id}`}>
                          <Reply className="w-3 h-3 mr-1" /> Respuesta
                        </Button>
                      )}
                      {!rec.completado && (
                        <Button size="sm" variant="ghost" onClick={() => handleComplete(rec)} className="h-9 px-2" title="Completado"><Check className="w-4 h-4 text-emerald-700" /></Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => handlePosponer(rec)} className="h-9 px-2" title="Posponer"><Clock className="w-4 h-4 text-stone-600" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(rec)} className="h-9 px-2" title="Editar"><Edit className="w-4 h-4 text-stone-600" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(rec.recordatorio_id)} className="h-9 px-2" title="Eliminar"><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dialog crear/editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-rec-wa">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar recordatorio" : "Nuevo recordatorio WhatsApp"}</DialogTitle>
            <DialogDescription className="text-xs">Define cuando recordarlo, a quien y que mensaje enviar.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label>Titulo *</Label>
                <Input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} placeholder="Ej: Recordar anticipo a Maria" data-testid="input-rec-titulo" />
              </div>
              <div>
                <Label>Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{tiposRec.map((t) => <SelectItem key={t} value={t} className="capitalize">{t.replace("_", " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Prioridad</Label>
                <Select value={form.prioridad} onValueChange={(v) => setForm({ ...form, prioridad: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{prioridades.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Fecha *</Label>
                <Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} data-testid="input-rec-fecha" />
              </div>
              <div>
                <Label>Hora</Label>
                <Input type="time" value={form.hora || ""} onChange={(e) => setForm({ ...form, hora: e.target.value })} />
              </div>
              <div>
                <Label>Cliente</Label>
                <Select value={form.cliente_id || "_none"} onValueChange={(v) => {
                  if (v === "_none") { setForm({ ...form, cliente_id: "", whatsapp_telefono: "" }); return; }
                  const c = clientes.find((x) => x.cliente_id === v);
                  setForm({ ...form, cliente_id: v, whatsapp_telefono: c?.whatsapp || c?.telefono || "" });
                }}>
                  <SelectTrigger data-testid="select-rec-cliente"><SelectValue placeholder="(opcional)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">— Sin cliente —</SelectItem>
                    {clientes.map((c) => <SelectItem key={c.cliente_id} value={c.cliente_id}>{c.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Evento</Label>
                <Select value={form.evento_id || "_none"} onValueChange={(v) => setForm({ ...form, evento_id: v === "_none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="(opcional)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">— Sin evento —</SelectItem>
                    {eventos.slice(0, 200).map((e) => <SelectItem key={e.evento_id} value={e.evento_id}>{e.nombre} - {e.fecha}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2 border-t border-stone-200 pt-3">
                <Label className="text-sm font-semibold text-stone-700">Mensaje WhatsApp</Label>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Plantilla (opcional)</Label>
                <Select value={form.whatsapp_plantilla_id || "_none"} onValueChange={(v) => v === "_none" ? setForm({ ...form, whatsapp_plantilla_id: "" }) : onSelectPlantilla(v)}>
                  <SelectTrigger><SelectValue placeholder="Sin plantilla" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">— Sin plantilla —</SelectItem>
                    {plantillas.filter((t) => t.activa).map((t) => <SelectItem key={t.template_id} value={t.template_id}>{t.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Telefono WhatsApp</Label>
                <Input value={form.whatsapp_telefono || ""} onChange={(e) => setForm({ ...form, whatsapp_telefono: e.target.value })} placeholder="+529991234567" data-testid="input-rec-tel" />
              </div>
              <div>
                <Label className="text-xs">Responsable</Label>
                <Input value={form.responsable || ""} onChange={(e) => setForm({ ...form, responsable: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Mensaje (variables: [nombre_cliente], [fecha_evento], [saldo], [anticipo])</Label>
                <Textarea rows={4} value={form.whatsapp_mensaje || ""} onChange={(e) => setForm({ ...form, whatsapp_mensaje: e.target.value })} className="font-mono text-xs" data-testid="input-rec-mensaje" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Notas internas (no se envian)</Label>
                <Textarea rows={2} value={form.notas || ""} onChange={(e) => setForm({ ...form, notas: e.target.value })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="h-11">Cancelar</Button>
            <Button onClick={handleSave} className="bg-emerald-900 hover:bg-emerald-800 h-11 px-6" data-testid="btn-guardar-rec-wa">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecordatoriosWhatsApp;
